import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TablePagination,
  Tooltip,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { Edit, Delete, Folder, Warning, SwapHoriz } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LOAN_LABELS = { custody: 'En custodia', loan: 'En préstamo', returned: 'Devuelto' };
const LOAN_COLORS = { custody: 'info', loan: 'warning', returned: 'success' };

const ProceedingTable = ({ proceedings, onEdit, onDelete, page, onPageChange, pagination, canView, canEdit, canDelete, sortBy, sortDir, onSortChange }) => {
  const navigate = useNavigate();

  if (!proceedings || proceedings.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Folder sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay expedientes registrados</p>
      </Paper>
    );
  }

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1);
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('es-ES', { timeZone: 'UTC' });

  const addYears = (date, years) => {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years);
    return d;
  };

  const getRetentionInfo = (proc) => {
    const rl = proc.retentionLine;
    if (!rl || !proc.startDate) return null;
    const now = new Date();
    const transferDate = addYears(proc.startDate, rl.localRetention);
    const eliminationDate = addYears(proc.startDate, rl.localRetention + rl.centralRetention);
    const daysToTransfer = Math.ceil((transferDate - now) / (1000 * 60 * 60 * 24));
    const daysToElimination = rl.dispositionE ? Math.ceil((eliminationDate - now) / (1000 * 60 * 60 * 24)) : null;
    return { transferDate, eliminationDate, daysToTransfer, daysToElimination, dispositionE: rl.dispositionE };
  };

  const SortCell = ({ label, field, sx: extraSx }) => (
    <TableCell onClick={() => onSortChange(field)} sx={{ cursor: 'pointer', userSelect: 'none', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortCell label="Nombre" field="name" />
              <SortCell label="Código" field="code" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <SortCell label="Tabla de Retención" field="retentionLine.retention.name" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
              <SortCell label="Fecha Inicial" field="startDate" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Retención</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proceedings.map((proceeding) => (
              <TableRow
                key={proceeding.id}
                hover
                onClick={() => navigate(`/proceedings/${proceeding.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Folder color="primary" />
                    <strong>{proceeding.name}</strong>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip label={proceeding.code} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {proceeding.retentionLine ? (
                    <Box>
                      <div style={{ fontWeight: 500, fontSize: '0.8rem' }}>{proceeding.retentionLine.series}</div>
                      <small style={{ color: 'gray' }}>{proceeding.retentionLine.subseries}</small>
                      <div style={{ color: '#888', fontSize: '0.7rem' }}>{proceeding.retentionLine.retention?.name}</div>
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatDate(proceeding.startDate)}</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  {(() => {
                    const info = getRetentionInfo(proceeding);
                    if (!info) return <Typography variant="caption" color="text.disabled">—</Typography>;
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Tooltip title={`Trasladar: ${formatDate(info.transferDate)} (${info.daysToTransfer} días)`}>
                          <Chip
                            icon={<SwapHoriz sx={{ fontSize: 14 }} />}
                            label={`${info.daysToTransfer}d`}
                            size="small"
                            color={info.daysToTransfer <= 0 ? 'error' : info.daysToTransfer <= 90 ? 'warning' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        </Tooltip>
                        {info.dispositionE && (
                          <Tooltip title={`Eliminar: ${formatDate(info.eliminationDate)} (${info.daysToElimination} días)`}>
                            <Chip
                              icon={<Warning sx={{ fontSize: 14 }} />}
                              label={`${info.daysToElimination}d`}
                              size="small"
                              color={info.daysToElimination <= 0 ? 'error' : info.daysToElimination <= 90 ? 'error' : 'default'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={LOAN_LABELS[proceeding.loan || 'custody'] || proceeding.loan}
                      color={LOAN_COLORS[proceeding.loan || 'custody'] || 'default'}
                      size="small"
                    />
                    {proceeding.endDate && <Chip label="Cerrado" size="small" />}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); onEdit(proceeding); }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); onDelete(proceeding.id); }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={(page || 1) - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit || 10}
          rowsPerPageOptions={[10]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}
    </Paper>
  );
};

export default ProceedingTable;
